import { css } from '@emotion/react';
import React from 'react';

import { Text } from '../Typography';
import type { TextProps } from '../Typography/Text';

const MAX_LINES_DEFAULT = 5;

const inlineToggleButtonStyles = css`
  display: inline;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: var(--text-primary);
  font: inherit;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
`;

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

const trimDescriptionCandidate = (value: string) => {
  const trimmedValue = value.trim();
  const lastWhitespaceIndex = trimmedValue.lastIndexOf(' ');

  return lastWhitespaceIndex > 0
    ? trimmedValue.slice(0, lastWhitespaceIndex)
    : trimmedValue;
};

type ExpandableInlineTextProps = {
  text: string;
  lines?: number;
  size?: TextProps['size'];
};

const ExpandableInlineText = ({
  text,
  lines = MAX_LINES_DEFAULT,
  size = 'xs',
}: ExpandableInlineTextProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [hasCollapsedOverflow, setHasCollapsedOverflow] = React.useState(false);
  const [collapsedText, setCollapsedText] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const textHostRef = React.useRef<HTMLDivElement>(null);
  const normalizedText = text.trim();

  React.useEffect(() => {
    setIsExpanded(false);
  }, [normalizedText]);

  useIsomorphicLayoutEffect(() => {
    if (!normalizedText || !containerRef.current || !textHostRef.current) {
      setHasCollapsedOverflow(false);
      setCollapsedText('');
      return;
    }

    const sourceTextElement = textHostRef.current
      .firstElementChild as HTMLElement | null;

    if (!sourceTextElement) {
      return;
    }

    const updateCollapsedText = () => {
      if (!containerRef.current) {
        return;
      }

      const targetWidth = containerRef.current.clientWidth;

      if (!targetWidth) {
        return;
      }

      const computedStyles = window.getComputedStyle(sourceTextElement);
      const lineHeight = Number.parseFloat(computedStyles.lineHeight) || 16;
      const maxHeight = lineHeight * lines + 1;
      const measurementElement = document.createElement('p');

      measurementElement.style.position = 'absolute';
      measurementElement.style.left = '-9999px';
      measurementElement.style.top = '0';
      measurementElement.style.visibility = 'hidden';
      measurementElement.style.pointerEvents = 'none';
      measurementElement.style.zIndex = '-1';
      measurementElement.style.width = `${targetWidth}px`;
      measurementElement.style.margin = '0';
      measurementElement.style.padding = '0';
      measurementElement.style.border = '0';
      measurementElement.style.whiteSpace = 'normal';
      measurementElement.style.wordBreak = 'break-word';
      measurementElement.style.overflowWrap = 'break-word';
      measurementElement.style.fontFamily = computedStyles.fontFamily;
      measurementElement.style.fontSize = computedStyles.fontSize;
      measurementElement.style.fontWeight = computedStyles.fontWeight;
      measurementElement.style.fontStyle = computedStyles.fontStyle;
      measurementElement.style.lineHeight = computedStyles.lineHeight;
      measurementElement.style.letterSpacing = computedStyles.letterSpacing;
      measurementElement.style.color = computedStyles.color;

      const createToggleElement = () => {
        const buttonElement = document.createElement('button');

        buttonElement.type = 'button';
        buttonElement.textContent = 'Read More';
        buttonElement.style.display = 'inline';
        buttonElement.style.margin = '0';
        buttonElement.style.padding = '0';
        buttonElement.style.border = '0';
        buttonElement.style.background = 'none';
        buttonElement.style.color = 'inherit';
        buttonElement.style.font = 'inherit';
        buttonElement.style.fontWeight = '600';
        buttonElement.style.lineHeight = 'inherit';
        buttonElement.style.whiteSpace = 'nowrap';
        buttonElement.style.textDecoration = 'underline';
        buttonElement.style.textUnderlineOffset = '2px';

        return buttonElement;
      };

      const fitsWithinMaxLines = (candidate: string) => {
        measurementElement.replaceChildren();
        measurementElement.append(document.createTextNode(`${candidate}... `));
        measurementElement.append(createToggleElement());

        return measurementElement.getBoundingClientRect().height <= maxHeight;
      };

      document.body.appendChild(measurementElement);

      try {
        measurementElement.textContent = normalizedText;

        if (measurementElement.getBoundingClientRect().height <= maxHeight) {
          setHasCollapsedOverflow(false);
          setCollapsedText(normalizedText);
          return;
        }

        let low = 0;
        let high = normalizedText.length;
        let bestFit = '';

        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          const candidate = trimDescriptionCandidate(
            normalizedText.slice(0, mid)
          );

          if (!candidate) {
            low = mid + 1;
            continue;
          }

          if (fitsWithinMaxLines(candidate)) {
            bestFit = candidate;
            low = mid + 1;
          } else {
            high = mid - 1;
          }
        }

        setHasCollapsedOverflow(true);
        setCollapsedText(bestFit || trimDescriptionCandidate(normalizedText));
      } finally {
        measurementElement.remove();
      }
    };

    updateCollapsedText();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateCollapsedText);
      resizeObserver.observe(containerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener('resize', updateCollapsedText);

    return () => {
      window.removeEventListener('resize', updateCollapsedText);
    };
  }, [lines, normalizedText]);

  const visibleText =
    !isExpanded && hasCollapsedOverflow ? collapsedText : normalizedText;

  return (
    <div ref={containerRef}>
      <div ref={textHostRef}>
        <Text size={size}>
          {visibleText}
          {!isExpanded && hasCollapsedOverflow && '... '}
          {(hasCollapsedOverflow || isExpanded) && (
            <>
              {' '}
              <button
                type="button"
                css={inlineToggleButtonStyles}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsExpanded((current) => !current);
                }}
              >
                {isExpanded ? 'Show Less' : 'Read More'}
              </button>
            </>
          )}
        </Text>
      </div>
    </div>
  );
};

export default ExpandableInlineText;
